// Recipe service
// - Proxies recipe search and detail requests through the spoonacular-proxy edge function
// - The Spoonacular API key lives server-side only — never exposed to the browser
// - Applies simple client-side caching via sessionStorage to reduce edge function invocations
// - Surfaces errors as thrown Errors for the caller to handle
// - Shows a toast when the edge function serves stale cached results due to
//   Spoonacular quota exhaustion or rate limiting

import toast from "react-hot-toast";
import { supabase } from "../lib/supabase";

export interface Recipe {
  id: number;
  title: string;
  image: string;
  usedIngredients: Array<{ name: string; amount: number; unit: string }>;
  missedIngredients: Array<{ name: string; amount: number; unit: string }>;
  usedIngredientCount: number;
  missedIngredientCount: number;
}

// Minimal representation of a step in analyzed instructions
export interface RecipeStep {
  number: number;
  step: string;
}

// Ingredient shape returned by the API for detailed recipes
export interface RecipeIngredient {
  id: number;
  name: string;
  original: string;
  amount: number;
  unit: string;
  measures: {
    us: {
      amount: number;
      unitShort: string;
      unitLong: string;
    };
    metric: {
      amount: number;
      unitShort: string;
      unitLong: string;
    };
  };
}

// Full recipe details returned by the API
export interface RecipeDetails {
  id: number;
  title: string;
  readyInMinutes: number;
  servings: number;
  sourceUrl: string;
  image: string;
  extendedIngredients: RecipeIngredient[];
  instructions: string;
  analyzedInstructions: {
    name: string;
    steps: RecipeStep[];
  }[];
}

// Public recipe utility methods
// - `findRecipes` searches by ingredients and caches results per ingredient set and pagination
// - `getRecipeDetails` fetches full recipe info and caches by recipe id
export const recipeService = {
  async findRecipes(
    ingredients: string[],
    offset = 0,
    number = 12,
  ): Promise<Recipe[]> {
    if (ingredients.length === 0) {
      throw new Error("Please select at least one ingredient");
    }

    const cacheKey = `recipes_${[...ingredients].sort().join(",")}_${offset}_${number}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error("🔴 Failed to read from sessionStorage:", error);
    }

    const { data, error } = await supabase.functions.invoke(
      "spoonacular-proxy",
      {
        body: { action: "findByIngredients", ingredients, offset, number },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to fetch recipes");
    }

    const { results, stale } = data as { results: Recipe[]; stale: boolean };

    if (stale) {
      toast("Showing cached results — recipe search is temporarily limited", {
        id: "stale-cache-warning",
      });
    }

    const recipes: Recipe[] = results;

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(recipes));
    } catch (error) {
      console.error("🔴 Failed to write to sessionStorage:", error);
    }

    return recipes;
  },

  async getRecipeDetails(recipeId: number): Promise<RecipeDetails> {
    const cacheKey = `recipe_details_${recipeId}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error("🔴 Failed to read from sessionStorage:", error);
    }

    const { data, error } = await supabase.functions.invoke(
      "spoonacular-proxy",
      {
        body: { action: "getRecipeDetails", recipeId },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to fetch recipe details");
    }

    const { results, stale } = data as {
      results: RecipeDetails;
      stale: boolean;
    };

    if (stale) {
      toast("Showing cached results — recipe search is temporarily limited", {
        id: "stale-cache-warning",
      });
    }

    const recipeDetails: RecipeDetails = results;

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(recipeDetails));
    } catch (error) {
      console.error("🔴 Failed to write to sessionStorage:", error);
    }

    return recipeDetails;
  },
};
